import { useState } from 'react';
import { Info } from 'lucide-react';
import ContactInstitutionModal from './ContactInstitutionModal';

/**
 * FeeDisplay — single source of truth for course-fee rendering on every
 * student-facing surface. Reads the server-side `fee_display` contract:
 *
 *   "amount"  → render A$XX,XXX/yr in the app's primary text colour.
 *               Drop the legacy orange/amber treatment.
 *   anything else (default "contact") → render the navy "Contact for fees"
 *               chip; click → opens the ContactInstitutionModal with this
 *               course pre-attached so the institution sees what was asked.
 *
 * Props:
 *   course        — course doc/normalized item. Must carry fee_display,
 *                   display_fee_aud, university_id, name (for the modal).
 *   institution   — optional institution doc used by the modal header when
 *                   the chip is launched outside a course context.
 *   className     — wrapper utility classes (margin etc.)
 *   amountSize    — Tailwind text-size class for the amount; default "text-lg"
 *   amountWeight  — Tailwind weight; default "font-extrabold"
 *   stopBubble    — if true (default for in-card chips), clicking the chip
 *                   does NOT propagate to the card's parent click handler.
 */
export default function FeeDisplay({
  course,
  institution = null,
  className = '',
  amountSize = 'text-lg',
  amountWeight = 'font-extrabold',
  stopBubble = true,
}) {
  const [open, setOpen] = useState(false);

  const fee = course?.display_fee_aud;
  const showAmount = course?.fee_display === 'amount' && fee > 0;

  if (showAmount) {
    return (
      <span
        className={`${amountSize} ${amountWeight} text-slate-900 font-mono whitespace-nowrap ${className}`}
        data-testid="fee-display-amount"
      >
        A${Number(fee).toLocaleString()}<span className="text-[11px] font-bold text-slate-500 ml-0.5">/yr</span>
      </span>
    );
  }

  const handleClick = (e) => {
    if (stopBubble) {
      e.stopPropagation();
      e.preventDefault();
    }
    setOpen(true);
  };

  return (
    <>
      <button
        type="button"
        onClick={handleClick}
        className={`inline-flex items-center gap-1 whitespace-nowrap transition-colors hover:brightness-95 ${className}`}
        style={{
          background: '#E6F1FB',
          color: '#0C447C',
          border: '0.5px solid #85B7EB',
          borderRadius: 20,
          fontSize: 12,
          fontWeight: 500,
          padding: '3px 12px',
        }}
        data-testid="fee-display-chip"
        aria-label="Contact institution for fees"
      >
        <Info className="h-3.5 w-3.5" />
        Contact for fees
      </button>
      {open && (
        <ContactInstitutionModal
          course={course}
          institution={institution}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}
